import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Formatters for the active locale — pass `locale.value` from `useI18n()`, never a literal.
 */

/**
 * A `YYYY-MM-DD` calendar day, e.g. "Sep 5". Read as UTC so the day never shifts with the
 * viewer's timezone.
 */
export const formatCalendarDay = ({ date, locale, withYear = false }: { date: string; locale: string; withYear?: boolean }): string =>
  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  }).format(new Date(date));

/**
 * A UTC instant as a date with its year, in the viewer's timezone. Takes the GraphQL `DateTime`
 * wire format (`YYYY-MM-DD HH:mm:ss`, no offset) as well as ISO strings.
 */
export const formatInstantDate = ({ date, locale }: { date: string; locale: string }): string =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(dayjs.utc(date).toDate());

export const formatNumber = ({ value, locale }: { value: number; locale: string }): string =>
  new Intl.NumberFormat(locale).format(value);

/** A whole-number percentage, e.g. `45` → "45%". */
export const formatPercent = ({ percent, locale }: { percent: number; locale: string }): string =>
  new Intl.NumberFormat(locale, { style: 'percent' }).format(percent / 100);

export const formatList = ({ items, locale }: { items: string[]; locale: string }): string =>
  new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);
